import { useState } from 'react';
import {
    guardarOtrosDocumentosDeEmpleado,
    obtenerDocumentosPorEmpleado,
    eliminarDocumento,
    getDocumentoCabByEmpleadoYTipo,
    guardarDocumentosCab,
    modificarDocumentosCab,
    anulaDocumentoCab
} from '../../../services/empleado/empleadoDocumentoService';
import { formatToJava } from '../../../util/dateUtils';

const emptyOtrosDocs = {
    id: null,
    categoria_id: null,
    numero: '',
    descripcion: '',
    fecha_inicio: null,
    fecha_fin: null
};

// Helper de fechas
const convertirFecha = (f) => {
    if (!f) return null;
    if (Array.isArray(f)) return new Date(f[0], f[1] - 1, f[2]);
    if (typeof f === 'object' && f.year) return new Date(f.year, f.monthValue - 1, f.dayOfMonth);
    if (typeof f === 'string') return new Date(f);
    return new Date(f);
};

export const useOtrosDocs = ({ empleadoId, toast, configMap, listas }) => {
    // 1. Estados
    const [otrosDocsList, setOtrosDocsList] = useState([]);
    const [otrosDocsData, setOtrosDocsData] = useState(emptyOtrosDocs);
    const [otrosDocsDialog, setOtrosDocsDialog] = useState(false); // Reemplaza a modals.addOtrosDocs
    const [otrosDocsFile, setOtrosDocsFile] = useState([]);

    const [loading, setLoading] = useState(false);

    const [procesaOtrosDocs, setProcesaOtrosDocs] = useState(false);

    // 2. Carga de Documentos
    const recargarOtrosDocs = async (id) => {
        if (!id) {
            setOtrosDocsList([]);
            return;
        }

        try {
            const docs = await obtenerDocumentosPorEmpleado(id);

            // A. Estrategia de Filtrado
            // 1. IDs de categorías válidas (Contrato, Certificado, etc.)
            const idsCategoriasOtros =
                listas?.tiposOtrosDocs?.map((c) => c.configuracionDetId) || [];

            // 2. ID del Formulario Padre (si existe configuración)
            const configFormOtros = listas?.tiposOtrosDocs?.find(
                (d) => d.codigo === configMap.FORM_OTROS_DOCS
            );
            const ID_FORM_OTROS = configFormOtros ? configFormOtros.configuracionDetId : null;

            const misOtros = docs.filter((d) => {
                // Caso 1: Tiene una categoría válida de la lista
                if (d.categoriaId && idsCategoriasOtros.includes(parseInt(d.categoriaId)))
                    return true;

                // Caso 2: Coincide con el ID del tipo de documento padre
                if (ID_FORM_OTROS && d.tipoDocumentoId === ID_FORM_OTROS) return true;

                // Caso 3: Coincide por código de texto (Respaldo)
                if (d.tipoDocumentoId === configMap.FORM_OTROS_DOCS) return true;

                return false;
            });

            // B. Mapeo
            setOtrosDocsList(
                misOtros.map((doc) => ({
                    id: doc.id,
                    categoria_id: doc.categoriaId ? parseInt(doc.categoriaId) : null,
                    numero: doc.referencia,
                    descripcion: doc.descripcion,
                    fecha_inicio: convertirFecha(doc.fechaInicio),
                    fecha_fin: convertirFecha(doc.fechaFin),
                    archivoUrl: doc.url
                }))
            );
        } catch (e) {
            console.error('Error recargando otros documentos:', e);
        }
    };

    // 3. Acciones del Modal
    const openNewOtrosDocs = () => {
        setOtrosDocsData(emptyOtrosDocs);
        setOtrosDocsFile(null);
        setOtrosDocsDialog(true);
    };

    const openEditOtrosDocs = (rowData) => {
        setOtrosDocsData({
            id: rowData.id,
            categoria_id: rowData.categoria_id,
            numero: rowData.numero,
            descripcion: rowData.descripcion,
            fecha_inicio: rowData.fecha_inicio,
            fecha_fin: rowData.fecha_fin,
            categoriaId: rowData.categoriaId
        });
        setOtrosDocsFile(null);
        setOtrosDocsDialog(true);
    };

    // 4. Guardar
    const saveOtrosDocs = async () => {
        if (loading) return;
        if (!empleadoId) {
            toast.current.show({
                severity: 'warn',
                summary: 'Atención',
                detail: 'Guarde el empleado primero.'
            });
            return;
        }

        // Validación: Archivo obligatorio solo si es nuevo
        if (!otrosDocsData.id && !otrosDocsFile) {
            toast.current.show({
                severity: 'error',
                summary: 'Error',
                detail: 'El archivo es obligatorio.'
            });
            return;
        }

        const formatearFecha = (d) => (d && d.toISOString ? d.toISOString().split('T')[0] : null);

        console.log('Guardando Otros Docs con datos:', otrosDocsData, 'y archivo:', otrosDocsFile);
        const payload = {
            id: otrosDocsData.id,
            codigoTipoDocumento: configMap.FORM_OTROS_DOCS,
            categoriaId: otrosDocsData.categoria_id,
            referenciaDetalle: otrosDocsData.numero,
            descripcion: otrosDocsData.descripcion,
            fechaInicio: formatearFecha(otrosDocsData.fecha_inicio),
            fechaFin:
                formatearFecha(otrosDocsData.fecha_fin) ||
                formatearFecha(otrosDocsData.fecha_inicio)
        };

        try {
            setLoading(true);
            await guardarOtrosDocumentosDeEmpleado(empleadoId, payload, otrosDocsFile);
            toast.current.show({
                severity: 'success',
                summary: 'Éxito',
                detail: 'Documento guardado.'
            });

            setOtrosDocsDialog(false);
            setOtrosDocsData(emptyOtrosDocs);
            setOtrosDocsFile(null);

            await recargarOtrosDocs(empleadoId);
        } catch (e) {
            console.error(e);
            toast.current.show({
                severity: 'error',
                summary: 'Error',
                detail: 'No se pudo guardar.'
            });
        } finally {
            setLoading(false);
        }
    };

    // 5. Eliminar
    const deleteOtrosDocs = async (rowData) => {
        if (!window.confirm('¿Estás seguro de eliminar este documento?')) return;

        try {
            await eliminarDocumento(rowData.id);
            toast.current.show({
                severity: 'success',
                summary: 'Eliminado',
                detail: 'Documento eliminado.'
            });
            setOtrosDocsList((prev) => prev.filter((item) => item.id !== rowData.id));
        } catch (error) {
            console.error('Error al eliminar documento:', error);
            toast.current.show({
                severity: 'error',
                summary: 'Error',
                detail: 'No se pudo eliminar.'
            });
        }
    };

    //// nuevo codigo BEGIN
    const cargaOtrosDocs = async (empleadoId) => {
        const CODE_TIPOREGISTRO_OTROSDOCS = 'NOM-0000-0015-0004';
        try {
            const data = await getDocumentoCabByEmpleadoYTipo(
                empleadoId,
                CODE_TIPOREGISTRO_OTROSDOCS
            );
            const lista = data.map((item) => ({
                id: item.documentoCabId,
                categoria_id: item.categoriaConfDet.configuracionDetId,
                numero: item.referenciaDetalle,
                descripcion: item.descripcion,
                fecha_inicio: convertirFecha(item.fechaInicio),
                fecha_fin: convertirFecha(item.fechaFin),
                archivoUrl: ''
            }));
            setOtrosDocsList(lista);
        } catch (error) {
            console.error('Error al Obtener Licencias:', error);
        }
    };
    const guardaOtrosDocs = async () => {
        if (loading) return;
        if (!empleadoId) {
            toast.current.show({
                severity: 'warn',
                summary: 'Atención',
                detail: 'Guarde el empleado primero.'
            });
            return;
        }

        // Validación: Archivo obligatorio solo si es nuevo
        if (!otrosDocsData.id && !otrosDocsFile) {
            toast.current.show({
                severity: 'error',
                summary: 'Error',
                detail: 'El archivo es obligatorio.'
            });
            return;
        }

        const CODE_TIPOREGISTRO_OTROSDOCS = 'NOM-0000-0015-0004';
        setProcesaOtrosDocs(true);
        try {
            const objGuardar = {
                entidadId: empleadoId,
                nombreEntidad: 'EMPLEADO',
                categoriaConfDetId: otrosDocsData.categoriaId,
                referenciaDetalle: otrosDocsData.numero,
                descripcion: otrosDocsData.descripcion,
                fechaInicio: formatToJava(otrosDocsData.fecha_inicio),
                fechaFin: formatToJava(otrosDocsData.fecha_fin),
                fechaEmision: '',
                codigoTipoDocumento: CODE_TIPOREGISTRO_OTROSDOCS
            };
            if (otrosDocsData.id) {
                objGuardar['documentoCabId'] = otrosDocsData.id;
                const data = await modificarDocumentosCab(objGuardar, otrosDocsFile);
            } else {
                const data = await guardarDocumentosCab(objGuardar, otrosDocsFile);
            }

            toast.current.show({
                severity: 'success',
                summary: 'Éxito',
                detail: 'Documento guardado.'
            });

            setOtrosDocsDialog(false);
            setOtrosDocsData(emptyOtrosDocs);
            setOtrosDocsFile(null);

            await cargaOtrosDocs(empleadoId);
        } catch (error) {
            console.error('Error al Guardar Otros Documentos:', error);
            toast.current.show({
                severity: 'error',
                summary: 'Error',
                detail: 'No se han guardado las Licencias'
            });
        } finally {
            setProcesaOtrosDocs(false);
        }
    };

    const anulaOtrosDocs = async (rowData) => {
        if (!window.confirm('¿Estás seguro de eliminar este documento?')) return;

        try {
            const data = await anulaDocumentoCab(rowData.id);
            toast.current.show({
                severity: 'success',
                summary: 'Eliminado',
                detail: 'Licencia eliminada.'
            });

            // Actualización optimista o recarga
            setOtrosDocsList((prev) => prev.filter((item) => item.id !== rowData.id));
        } catch (error) {
            console.error('Error eliminando licencia:', error);
            toast.current.show({
                severity: 'error',
                summary: 'Error',
                detail: 'No se pudo eliminar.'
            });
        }
    };

    //// nuevo codigo END

    return {
        otrosDocsList,
        otrosDocsData,
        setOtrosDocsData,
        otrosDocsDialog,
        setOtrosDocsDialog,
        otrosDocsFile,
        setOtrosDocsFile,
        recargarOtrosDocs,
        openNewOtrosDocs,
        openEditOtrosDocs,
        saveOtrosDocs,
        deleteOtrosDocs,
        setOtrosDocsList,
        loading,
        cargaOtrosDocs,
        guardaOtrosDocs,
        procesaOtrosDocs,
        anulaOtrosDocs
    };
};
